import { useCallback, useEffect, useMemo, useState } from 'react'

import { useUpdateCheckpoint } from '../hooks/useUpdateCheckpoint'
import { usePermissions } from '@/hooks/usePermissions'
import { useForm } from 'react-hook-form'
import { HiOutlineCheckCircle } from 'react-icons/hi'
import { useSelector } from 'react-redux'

import {
  Box,
  Divider,
  FormLabel,
  Spacer,
  Spinner,
  Text,
} from '@chakra-ui/react'

import {
  Button,
  DrawerScrollContainer,
  FormInputControl,
} from '@/components/ui'
import { CheckpointIProps } from '@/features/floor-plan'
import { selectEditMode } from '@/redux/ui/uiSlice'

import { CheckpointTaskInputI } from '../types/types'
import { hasChecklistUpdated, hasDuplicateChecklistItems } from '../utils/utils'
import { CheckPointQRCode } from './CheckPointQRCode'
import { CheckpointChecklist } from './CheckpointChecklist'
import { EditCheckPointHeader } from './EditCheckPointHeader'
import { VerificationPhotoCheckbox } from './VerificationPhotoCheckbox'

interface EditCheckPointIProps {
  checkpoint: CheckpointIProps
  onDone: () => void
  onClose: () => void
}

export const EditCheckPoint = ({
  checkpoint,
  onClose,
  onDone,
}: EditCheckPointIProps) => {
  const { shouldEnableUpdateCheckpoint, shouldEnableCheckpointChecklist } =
    usePermissions()

  const isEditMode = useSelector(selectEditMode)

  const [checklistItems, setChecklistItems] = useState<CheckpointTaskInputI[]>(
    []
  )
  const [hasChecklistChanges, setHasChecklistChanges] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { isValid, errors },
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: checkpoint?.name || '',
      id: '',
      facility: '',
      floor: '',
    },
  })

  const watchedName = watch('name')

  const {
    areTasksRequired,
    areCheckpointTasksLoading,
    checkpointTasks,
    existingChecklistValues,
    hasRequiredChanged,
    hasPhotoRequiredChanged,
    isLoading,
    isPhotoRequired,
    setAreTasksRequired,
    handleIsPhotoRequired,
    updateCheckpoint,
    isGuardToursV2,
  } = useUpdateCheckpoint({
    checkpointId: checkpoint?.id,
    isPhotoRequiredDefault: checkpoint?.isPhotoRequired,
    onDone,
  })

  const hasDuplicates = useMemo(
    () => hasDuplicateChecklistItems(checklistItems),
    [checklistItems]
  )

  const hasValidInputs = isValid && !hasDuplicates
  const hasNameChanged = checkpoint?.name !== watchedName
  const hasChanges =
    hasNameChanged ||
    hasChecklistChanges ||
    hasRequiredChanged ||
    hasPhotoRequiredChanged

  const handleChecklistUpdate = useCallback(
    (formattedItems: CheckpointTaskInputI[]) => {
      setChecklistItems(formattedItems)

      // Calculate changes by comparing with original loaded data
      const hasChanges = hasChecklistUpdated(formattedItems, checkpointTasks)

      setHasChecklistChanges(hasChanges)
    },
    [checkpointTasks]
  )

  useEffect(() => {
    if (checkpoint) {
      reset({
        name: checkpoint?.name || '',
        id: checkpoint?.displayId || '',
        facility: checkpoint?.facility?.name || '',
        floor: checkpoint?.floor?.name || '',
      })
    }
  }, [checkpoint, reset])

  const onSubmit = async (values: { name: string }) => {
    await updateCheckpoint(
      checkpoint.id,
      values.name,
      areTasksRequired,
      checklistItems
    )
  }

  useEffect(() => {
    if (!isEditMode) {
      setValue('name', checkpoint?.name)
    }
  }, [isEditMode, setValue, checkpoint?.name])

  return (
    <>
      <EditCheckPointHeader onClose={onClose} />
      <DrawerScrollContainer padding='30px' paddingTop='20px'>
        <Box mb={3}>
          <FormInputControl
            data-testid='EditCheckPoint:name'
            errorMessage='You must specify a name'
            id='name'
            inputRef={register({
              required: 'You must specify a name',
              pattern: /\S/,
            })}
            isDisabled={!isEditMode}
            isInvalid={!!errors.name}
            label='Checkpoint name'
            type='name'
          />
        </Box>
        <Box mb={3}>
          <FormInputControl
            data-testid='EditCheckPoint:id'
            id='id'
            inputRef={register()}
            isDisabled={true}
            label='Checkpoint ID'
          />
        </Box>
        <Box mb={3}>
          <FormInputControl
            data-testid='EditCheckPoint:facility'
            id='facility'
            inputRef={register()}
            isDisabled={true}
            label='Facility'
            placeholder={checkpoint?.facility?.name}
          />
        </Box>
        <Box mb={3}>
          <FormInputControl
            data-testid='EditCheckPoint:floor'
            id='floor'
            inputRef={register()}
            isDisabled={true}
            label='Floor'
            placeholder={checkpoint?.floor?.name}
          />
        </Box>
        <FormLabel color='#5C6C7A' fontSize='16px' opacity={0.5}>
          QR Code
        </FormLabel>
        <CheckPointQRCode
          displayId={checkpoint?.displayId}
          id={checkpoint?.id}
          name={checkpoint?.name}
        />
        {isGuardToursV2 && (
          <Box>
            {areCheckpointTasksLoading ? (
              <Box
                alignItems='center'
                display='flex'
                justifyContent='center'
                py={4}
              >
                {shouldEnableCheckpointChecklist && (
                  <>
                    <Spinner
                      color='#298bbd'
                      emptyColor='gray.300'
                      mr={3}
                      my={5}
                      size='md'
                      thickness='4px'
                    />
                    <Text fontWeight={500}>Loading checklist...</Text>
                  </>
                )}
              </Box>
            ) : (
              <CheckpointChecklist
                areTasksRequired={areTasksRequired}
                existingValues={existingChecklistValues}
                hasDuplicateItems={hasDuplicates}
                isInEditMode={isEditMode}
                onRequiredChange={setAreTasksRequired}
                onUpdate={handleChecklistUpdate}
              />
            )}
            <VerificationPhotoCheckbox
              isChecked={isPhotoRequired}
              isDisabled={!isEditMode || !shouldEnableCheckpointChecklist}
              onRequiredChange={handleIsPhotoRequired}
            />
          </Box>
        )}
      </DrawerScrollContainer>

      <Spacer />

      {isEditMode && (
        <>
          <Divider
            bgColor='#D7D4D4'
            display='flex'
            h='2px'
            justifySelf='flex-end'
            mb='10px'
          />
          <Box
            display='flex'
            flexDir='row'
            justifyContent='space-between'
            mb='40px'
            padding='30px'
            paddingY='16px'
          >
            <Button mr='3' onClick={onClose} variant='ghost'>
              Cancel
            </Button>

            <Button
              isDisabled={
                !hasValidInputs ||
                !hasChanges ||
                !shouldEnableUpdateCheckpoint ||
                (hasChecklistChanges && !shouldEnableCheckpointChecklist)
              }
              isLoading={isLoading}
              leftIcon={<HiOutlineCheckCircle size='24px' />}
              loadingText='Saving Changes'
              onClick={handleSubmit(onSubmit)}
            >
              Save Changes
            </Button>
          </Box>
        </>
      )}
    </>
  )
}
